import { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  TablePagination,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Wrapper } from "../../components";
import {
  activePlansSummary,
  totalCreditsAndRemaining,
  creditAnalytics,
} from "../../services/creditsService";
import { apiCategortList } from "../../services";
import { Link } from "react-router-dom";

export function Credit() {
  const [creditsPerApiData, setCreditsPerApiData] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [remainingCredits, setRemainingCredits] = useState(0);
  const [monthlyUsage, setMonthlyUsage] = useState(0);
  const [res, setRes] = useState();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({});

  // 🔹 Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { user } = useSelector((state) => state.auth);

  const companyId = user.company.id;
  const jwtToken = user.jwt;

  // 🔹 Fetch active plan
  useEffect(() => {
    const fetchActivePLan = async () => {
      try {
        const res = await activePlansSummary({ id: companyId });
        setRes(res);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchActivePLan();
  }, [companyId]);

  // 🔹 Fetch credit analytics
  useEffect(() => {
    const fetchCreditAnalytics = async () => {
      try {
        const res = await creditAnalytics();
        setAnalyticsData(res.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching credit analytics:", error);
      }
    };

    fetchCreditAnalytics();
  }, []);

  // 🔹 Fetch credits summary
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await totalCreditsAndRemaining(jwtToken);
        setTotalCredits(res?.data?.credit?.totalAssignedCredit || 0);
        setRemainingCredits(
          parseFloat(Number(res?.data?.credit?.remainingCredit).toFixed(2)) || 0
        );
        setMonthlyUsage(
          (res?.data?.credit?.totalAssignedCredit || 0) -
            (res?.data?.credit?.remainingCredit || 0)
        );
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [jwtToken]);

  // 🔹 Fetch credits per API
  useEffect(() => {
    const fetchCreditsPerApi = async () => {
      try {
        const response = await apiCategortList();
        const apisByCategory = response.data?.data?.attributes?.apisByCategory;

        if (apisByCategory) {
          let globalIndex = 0;
          const creditsPerApiData: any[] = [];

          Object.values(apisByCategory).forEach((category: any) => {
            category.apis.forEach((api: any) => {
              globalIndex++;
              creditsPerApiData.push({
                serialNo: globalIndex.toString(),
                key: api.id,
                apisUsed: api.name,
                creditUtilised: api.creditConsumptionValue,
              });
            });
          });

          setCreditsPerApiData(creditsPerApiData);
        }
      } catch (error) {
        console.error("Error fetching credits per API:", error);
      }
    };

    fetchCreditsPerApi();
  }, [companyId]);

  // 🔹 Transactions mapping
  const transactions =
    analyticsData?.apiUsageLogs?.map((log) => ({
      date: log.date,
      type: "usage",
      description: log.apiName,
      amount: log.consumption,
      balance: log.balance,
      status: "completed",
    })).reverse() || [];

  // 🔹 Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 🔹 Slice transactions for pagination
  const paginatedTransactions = transactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const typeColors = {
    usage: { bgcolor: "grey.100", color: "grey.700" },
    purchase: { bgcolor: "primary.main", color: "white" },
    refund: { bgcolor: "green.100", color: "green.700" },
  };

  const roundTo2 = (num) =>
    Math.round((Number(num) + Number.EPSILON) * 100) / 100;

  return (
    <Wrapper>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={10}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 500, color: "#65758B", fontSize: "14px" }}
      >
        Monitor your API usage and manage credit balance
      </Typography>
        <Button
          variant="contained"
          component={Link}
          to="/contact-us"
          sx={{
            marginLeft: "auto",
            borderRadius: "8px",
            fontSize: { xs: "12px", sm: "14px", md: "16px" },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 0.8, sm: 1, md: 1.2 },
            minWidth: { xs: "100px", sm: "120px", md: "140px" },
          }}
        >
          + Buy Credits
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mt: 1 }} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontSize: "15px", color: "#020817", mb: 1 }}>
                Available Credits
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#03A9F4", fontSize: "25px", mb: 1 }}>
                {remainingCredits}
              </Typography>
              <Typography variant="caption" sx={{ color: "#65758B", fontSize: "12px" }}>
                {analyticsData?.availableCredits?.percentageChange
                  ? `${analyticsData.availableCredits.percentageChange > 0 ? "+" : ""}${analyticsData.availableCredits.percentageChange}% from last month`
                  : "+12% from last month"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontSize: "15px", color: "#020817", mb: 1 }}>
                Monthly Usage
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#03A9F4", fontSize: "25px", mb: 1 }}>
                {roundTo2(analyticsData?.monthlyUsage?.currentMonth ?? monthlyUsage)}
              </Typography>
              <Typography variant="caption" sx={{ color: "#65758B", fontSize: "12px" }}>
                {analyticsData?.monthlyUsage?.percentageChange
                  ? `${analyticsData.monthlyUsage.percentageChange > 0 ? "+" : ""}${analyticsData.monthlyUsage.percentageChange}% from last month`
                  : "-8% from last month"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontSize: "15px", color: "#020817", mb: 1 }}>
                Usage Limit
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#03A9F4", fontSize: "25px", mb: 1 }}>
                {analyticsData?.usageLimit?.usagePercentage || 0}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={analyticsData?.usageLimit?.usagePercentage || 0}
                sx={{ mt: 2, borderRadius: 2 }}
              />
              <Typography variant="caption" sx={{ color: "#65758B", fontSize: "12px" }}>
                of {analyticsData?.usageLimit?.totalActiveCredits || totalCredits} total credits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Box sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "#E1E7EF", boxShadow: 1, mt: 5 }}>
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "20px", mb: 0.5, color: "#020817" }}>
          Recent Transactions
          </Typography>
          <Typography>Your latest credit transactions and API usage</Typography>
        </Box>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Date</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Type</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Description</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Amount</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Balance</TableCell>
              <TableCell align="center" sx={{ fontSize: "1.5rem" }}>Status</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTransactions.map((txn, idx) => (
                <TableRow key={idx}>
                  <TableCell align="center" sx={{ fontSize: "1.3rem" }}>{txn.date}</TableCell>
                  <TableCell align="center">
                    <Chip label={txn.type} sx={{ ...typeColors[txn.type], fontSize: "1.3rem", px: 1 }} />
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "1.3rem" }}>{txn.description}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "1.3rem", fontWeight: 500, color: txn.amount <= 0 ? "success.main" : "error.main" }}>
                    {txn.amount}
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "1.3rem" }}>{txn.balance}</TableCell>
                  <TableCell align="center">
                    <Chip label={txn.status} sx={{ bgcolor: "grey.100", color: "grey.700", fontSize: "1.3rem", fontWeight: 500 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={transactions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "1.3rem", // adjust to match table font
              },
              "& .MuiTablePagination-select": {
                fontSize: "1.3rem",
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1.5rem", // pagination arrow size
              },
            }}
          />
        </Box>
      </Box>
    </Wrapper>
  );
}
